import React from 'react';
import styled from 'styled-components';
import ConfirmationBar from './shared/confirmation_bar';
import SlotPicker from './shared/slot_picker';
import DatePicker from './shared/date_picker';
import TimeZonePicker from './shared/time_zone_picker';
import InterviewDetails from './shared/interview_details';
import t from 'shared/utils/translation';
import { SectionHeader } from '@grnhse/seedling/lib/azalea/typography';
import Title from './desktop/title';
import SubmitSpinner from './shared/submit_spinner';
import CandidateViewShell from './shared/candidate_view_shell';
import AdditionalDetails from './shared/additional_details';
import type { Props } from './shared/candidate_view_shell';
import { SectionHeader as BonsaiSectionHeader } from '@grnhse/bonsai/lib/components/typography';
import { Bonsai } from '@grnhse/bonsai/lib/core';
import Logo from '../components/logo';

const LogoWrapper = styled.div`
  display: flex;
  margin-top: 65px;
  margin-bottom: -65px;

  @media only screen and (max-width: 1024px) {
    margin-left: 35px;
    margin-top: 35px;
    margin-bottom: -35px;
  }
`;

export default class CandidateView extends CandidateViewShell {
  constructor(props: Props) {
    super(props);

    this.state = {
      ...this.state,
      page: 0,
    };
  }

  changePage = (page: number) => {
    this.setState({ page });
  };

  render() {
    const {
      candidatePronunciation,
      candidatePronouns,
      jobTitle,
      interviewName,
      interviewLength,
      interviewLocation,
      interviewInvitation,
      isMobile,
      timeZones,
      useVideo,
      videoConferencingProvider,
      start,
      end,
      footer,
      isReschedule,
      bonsaiEnabled,
      bonsaiProps,
      logoUrl,
    } = this.props;
    const {
      submitting,
      selectedTimeZoneOption,
      selectedDate,
      currentMonth,
      selectedTime,
      page,
      availableSlotsByDay,
    } = this.state;

    const additionalDetails =
      !isReschedule &&
      (candidatePronunciation.display || candidatePronouns.display);

    return (
      <Bonsai configuration={bonsaiProps || {}}>
        <Container id="touch_screen_availability_widget">
          {bonsaiEnabled && logoUrl && (
            <LogoWrapper>
              <Logo logoUrl={logoUrl} />
            </LogoWrapper>
          )}
          {submitting && (
            <SubmitSpinner bonsaiEnabled={bonsaiEnabled} size="large" />
          )}
          <Title
            page={page}
            goBack={() => this.changePage(0)}
            isReschedule={isReschedule}
            bonsaiEnabled={bonsaiEnabled}
          />

          {page === 0 && (
            <FlexWrapper>
              <InterviewDetails
                bonsaiEnabled={bonsaiEnabled}
                jobTitle={jobTitle}
                interviewName={interviewName}
                interviewLength={interviewLength}
                interviewLocation={interviewLocation}
                interviewInvitation={interviewInvitation}
                useVideo={useVideo}
                videoConferencingProvider={videoConferencingProvider}
              />
              <DateSlotPickerWrapper>
                <SelectTimeTitle bonsaiEnabled={bonsaiEnabled}>
                  {t('self_schedule.select_date_time')}
                </SelectTimeTitle>
                <PickerWrapper>
                  <div>
                    <DatePicker
                      availableSlotsByDay={availableSlotsByDay}
                      currentMonth={currentMonth}
                      end={end}
                      selectedDate={selectedDate}
                      selectDate={(date) => {
                        this.setState({
                          selectedDate: date,
                          selectedTime: null,
                        });
                      }}
                      setCurrentMonth={(month) => {
                        this.setState({ currentMonth: month });
                      }}
                      start={start}
                      bonsaiEnabled={bonsaiEnabled}
                    />
                    <TimeZonePicker
                      timeZones={timeZones}
                      selectTimeZoneOption={this.handleTimeZoneChange}
                      selectedTimeZoneOption={selectedTimeZoneOption}
                    />
                  </div>
                  <SlotPicker
                    availableSlotsByDay={availableSlotsByDay}
                    selectTime={(slot) => {
                      this.setState({ selectedTime: slot });
                    }}
                    selectedTime={selectedTime}
                    selectedTimeZoneOption={selectedTimeZoneOption}
                    selectedDate={selectedDate}
                    bonsaiEnabled={bonsaiEnabled}
                  />
                </PickerWrapper>
              </DateSlotPickerWrapper>
            </FlexWrapper>
          )}

          {page === 1 && additionalDetails && (
            <AdditionalDetails
              candidatePronunciation={candidatePronunciation}
              candidatePronouns={candidatePronouns}
              isMobile={isMobile}
              footer={footer}
              bonsaiEnabled={bonsaiEnabled}
            />
          )}
        </Container>

        <ConfirmationBar
          interviewLength={interviewLength}
          selectedTime={selectedTime}
          selectedTimeZoneOption={selectedTimeZoneOption}
          submitting={submitting}
          next={additionalDetails && page === 0}
          isReschedule={isReschedule}
          submit={() => {
            if (additionalDetails && page === 0) {
              this.changePage(1);
            } else {
              this.submit();
            }
          }}
          bonsaiEnabled={bonsaiEnabled}
        />
      </Bonsai>
    );
  }
}

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FlexWrapper = styled.div`
  display: flex;

  @media only screen and (max-width: 1024px) {
    flex-direction: column;
  }
`;

const DateSlotPickerWrapper = styled.div`
  flex: 1 0 auto;
  margin-left: 80px;

  @media only screen and (max-width: 1024px) {
    margin-left: 0;
    position: relative;
    left: -35px;
    padding: 0 60px 100px;
  }

  @media only screen and (max-width: 480px) {
    position: relative;
    left: 0;
    padding: 0;
  }
`;

const SelectTimeTitle = ({
  children,
  bonsaiEnabled,
}: {
  children: React.ReactNode;
  bonsaiEnabled?: boolean;
}) => {
  if (bonsaiEnabled) {
    return (
      <div style={{ marginTop: 0, marginLeft: '35px' }}>
        <BonsaiSectionHeader>{children}</BonsaiSectionHeader>
      </div>
    );
  }

  return <SelectTimeSectionHeader>{children}</SelectTimeSectionHeader>;
};

const SelectTimeSectionHeader = styled(SectionHeader)`
  margin-top: 0;
  margin-left: 35px;
`;

const PickerWrapper = styled.div`
  margin-top: 20px;
  display: flex;

  @media only screen and (max-width: 1024px) {
    justify-content: space-between;
  }

  @media only screen and (max-width: 480px) {
    flex-direction: column;
  }
`;
